import React, { useEffect, useMemo, useState, memo } from 'react';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Trophy, Star, Clock, ArrowRight, Link as LinkIcon, TrendingUp, Lightbulb, Target, Flame, AlertTriangle, FileText, CalendarDays, History, Users, CheckCircle2 } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import { MODULES, getModuleIcon } from '../../../content';
import { getWeeklyStats } from '../../../utils/activityHelpers';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { type LearnerAccount, type ParentAccount, type AccountsState } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { useWeeklyActivity } from '../hooks/useWeeklyActivity';
import { useChildDecay } from '../hooks/useChildDecay';
import { useLatestAssessment } from '../hooks/useLatestAssessment';
import { useLinkChild } from '../hooks/useLinkChild';
import { useStore } from '../../../app/store';
import { DataFreshness } from '../../shared/DataFreshness';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getChildLastActivity } from '../shared/childActivity';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getParentTipDismissals, isTipDismissed } from '../../../data/domains/preferences';
interface HomePageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
/**
 * Parent portal Home — 10-second dashboard.
 *
 * Stat-ownership (audit §16):
 * - Level + XP + current streak → ONLY here (compact hero chips)
 * - Weekly rhythm → compact strip here; canonical detail lives on Activity
 * - Decay alerts → top 2 here; full list on Tips
 * - Top tip → 1 here; full list on Tips
 * - Latest assessment → compact chip here; detail on Vakken (Toetsen-route in Wave 3)
 * - Overall mastery / module details → NOT shown here (canonical: Vakken)
 * - Days/Q/Accuracy this week → NOT shown here (canonical: Activity)
 *
 * Anti-pattern removed (Wave 2): quick-nav stat cards that re-rendered
 * "Gemiddelde beheersing" and "Oefendagen 3/7" — these duplicated the
 * canonical pages a click away. Replaced with a slim explore-row.
 */
export const HomePage = memo(function HomePage({
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  linkParentToLearnerById,
  onNavigate
}: HomePageProps) {
  // All hooks MUST be called before any early return
  // W4-6: shared hook replaces inline useMemo (audit C-P-6)
  const {
    weeklyActivity
  } = useWeeklyActivity(childData);
  const weeklyStats = useMemo(() => getWeeklyStats(childData?.questionStates || {}), [childData?.questionStates]);
  const moduleMastery = useModuleMastery(selectedChild, getLearnerModuleMastery);
  // v1.6: decay-iteration extracted to useChildDecay hook (audit C-P-NEW-3).
  // Single source of truth for the family↔module mapping heuristic.
  const decay = useChildDecay(childData);
  // W4-2: respect parent-scoped tip dismissals on the Home preview slot.
  // Loaded async on mount + when the parent changes; empty map until ready.
  const [tipDismissals, setTipDismissals] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void getParentTipDismissals(activeAccount.id).then(next => {
      if (!cancelled) setTipDismissals(next);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccount.id]);
  // Decay alerts — max 2, stale only. Same data as TipsPage but capped tighter.
  const decayAlerts = useMemo(() => {
    if (!selectedChild) return [];
    const alerts: Array<{
      module: (typeof MODULES)[number];
      daysSince: number;
      status: 'fading' | 'stale';
    }> = [];
    for (const module of MODULES) {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      if (mastery <= 0) continue;
      const info = decay.perModule[module.id];
      if (!info || info.worstStatus !== 'stale') continue;
      alerts.push({
        module,
        daysSince: info.daysSince,
        status: info.worstStatus
      });
    }
    return alerts.sort((a, b) => b.daysSince - a.daysSince).slice(0, 2);
  }, [selectedChild?.id, decay.perModule, getLearnerModuleMastery]);
  // Top tip — most actionable single signal
  const topTip = useMemo(() => {
    if (!selectedChild || !childData) return null;
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    // Candidates share IDs with TipsPage so dismissals stay in sync.
    if (daysPracticed === 0 && !isTipDismissed(tipDismissals, 'rhythm-none')) {
      return {
        title: 'Start met een vast ritme',
        body: `Korte, dagelijkse sessies van 10 minuten werken beter dan één lange sessie.`,
        accent: 'amber' as const
      };
    }
    const weakest = moduleMastery.filter(m => m.mastery < 50).sort((a, b) => a.mastery - b.mastery)[0];
    if (weakest && !isTipDismissed(tipDismissals, `subject-weak-${weakest.module.id}`)) {
      return {
        title: `Extra aandacht voor ${weakest.module.title.toLowerCase()}`,
        body: `${selectedChild.displayName} oefent dit vak nog op ${weakest.mastery}%. Meester djay past het tempo automatisch aan.`,
        accent: 'rose' as const
      };
    }
    const currentStreak = childData.stats?.streakState?.currentStreak || 0;
    if (currentStreak >= 3 && !isTipDismissed(tipDismissals, 'streak-active')) {
      return {
        title: `${currentStreak} dagen op rij geoefend!`,
        body: `Een mooie streak. Stimuleer ${selectedChild.displayName} om de reeks vast te houden.`,
        accent: 'amber' as const
      };
    }
    return {
      title: 'Persoonlijke tips voor jullie',
      body: `Bekijk advies op maat over ritme, vakken en motivatie.`,
      accent: 'emerald' as const
    };
  }, [selectedChild?.id, childData, weeklyActivity, moduleMastery, tipDismissals]);
  // Latest assessment (compact chip — full detail on Vakken / future Toetsen route)
  // v1.6: extracted to useLatestAssessment hook (audit F-RUN-NEW-5).
  // v1.7: consume isLoading to render skeleton instead of flashing the empty
  // placeholder card on every page mount (audit P-2).
  const {
    report: latestAssessment,
    isLoading: isLoadingAssessment
  } = useLatestAssessment(selectedChild, selectedGroup);
  // Accounts uit de Zustand-store, via een selector zodat dit scherm meeloopt
  // wanneer er een kind wordt gekoppeld.
  const accountsState = useStore(s => s.multiAccountState.accounts);
  // Link child hook for empty state
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    warnings,
    hasGatingWarning,
    hasBlockingWarning,
    handleSubmit,
    handleConfirmLink,
    handleCancelLink
  } = useLinkChild({
    // `useAppStore` is een gewone hook rond de Zustand-store, geen store zelf —
    // `useAppStore.getState()` bestond niet en crashte hier na inloggen.
    // Via de store-selector is dit bovendien reactief: koppelt er een kind bij,
    // dan ziet dit scherm dat meteen. Met getState() was dat een bevroren
    // momentopname geweest.
    accounts: accountsState,
    parentId: activeAccount.id,
    linkedLearners,
    linkParentToLearnerById
  });
  // Empty state — no children linked
  if (linkedLearners.length === 0 || !selectedChild || !childData) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-8137" data-mp="3f9bv7rp96mfc">
        <div className="max-w-2xl mx-auto mt-12" data-id="element-8138" data-mp="3k0k2oi70vxpv">
          <div className="flex flex-col items-center mb-8" data-id="element-8139" data-mp="3k250qhsjpysh">
            <DjayNarrator size="lg" emotion="happy" animate data-id="element-8140" data-mp="3oy3jnpv3uoli" />
            <div className="mt-6 w-full max-w-sm" data-id="element-8141" data-mp="3t0qdfwbc6jlc">
              <DjayBubble message="Hoi! Ik ben Djay. Laten we samen de voortgang van je kind volgen!" position="top" colorScheme="emerald" autoDismissMs={0} data-id="element-8142" data-mp="3szff4c0nu41m" />
            </div>
          </div>
          <Card className="p-8 border-emerald-100 shadow-sm text-center bg-white" data-id="element-8143" data-mp="23mby22m7k92i">
            {success ? <div className="py-8 animate-in fade-in zoom-in duration-300" data-id="element-8144" data-mp="28ly8636qp973">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8145" data-mp="21146ys3b05nv">
                  <CheckCircle2 className="w-8 h-8" data-id="element-8146" data-mp="1w9vvzrmbsc8l" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-8147" data-mp="1rarmezvhochq">
                  Gelukt!
                </h2>
                <p className="text-emerald-700 font-medium" data-id="element-8148" data-mp="1mdsrwg7wy3j0">{success}</p>
              </div> : pendingLearner ? <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-8149" data-mp="2jy5mh6uhho62">
                <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-8150" data-mp="2f1x0pals6wpx">
                  Kind bevestigen
                </h2>
                <p className="text-slate-600 mb-6" data-id="element-8151" data-mp="2a3vfok4sonbt">
                  Weet je zeker dat je{' '}
                  <span className="font-bold text-slate-900" data-id="element-8152" data-mp="255bs6fajz3hc">
                    {pendingLearner.displayName}
                  </span>{' '}
                  wilt koppelen aan jouw account?
                </p>

                {hasGatingWarning && warnings.length > 0 && <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left flex gap-3" data-id="element-8153" data-mp="3qzqv19rb3jnp">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8154" data-mp="3lybb5l0v7j6w" />
                    <div data-id="element-8155" data-mp="4lzbums0o06z">
                      <h4 className="font-bold text-amber-900 text-sm" data-id="element-8156" data-mp="3vyu0gvb77rw7">
                        Let op
                      </h4>
                      <ul className="mt-1 space-y-1" data-id="element-8157" data-mp="djjf219fw6k8">
                        {warnings.map((w, i) => <li key={i} className="text-sm text-amber-800" data-id="element-8158" data-mp="9hxya4tsb84r" data-mpi={i}>
                            {w.message}
                          </li>)}
                      </ul>
                    </div>
                  </div>}

                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-8159" data-mp="2p9vhbcqq2nnz">
                  <Button variant="secondary" onClick={handleCancelLink} className="w-full sm:w-auto" data-id="element-8160" data-mp="2pbyfik213n3s">
                    Annuleren
                  </Button>
                  <Button onClick={handleConfirmLink} disabled={hasBlockingWarning} className={`w-full sm:w-auto text-white font-bold ${hasGatingWarning ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-id="element-8161" data-mp="14scke4cxpizw">
                    {hasGatingWarning ? 'Toch koppelen' : 'Bevestigen'}
                  </Button>
                </div>
              </div> : <div className="animate-in fade-in duration-300" data-id="element-8162" data-mp="74c9jg9xj8g2">
                <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-8163" data-mp="765gkwogfuol">
                  Koppel je kind
                </h2>
                <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-8164" data-mp="21ic3gsqpnzwq">
                  Vul de koppelcode van je kind in om de voortgang te kunnen
                  volgen. Je kunt deze code vinden in de instellingen van het
                  leerling-account.
                </p>
                <form onSubmit={handleSubmit} className="max-w-xs mx-auto" data-id="element-8165" data-mp="24y6jktm9plqe">
                  <div className="space-y-4" data-id="element-8166" data-mp="200eppu22bpjk">
                    <Input value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder="Bijv. A1B2C3D4" className="text-center text-lg tracking-widest uppercase font-mono bg-slate-50" maxLength={8} data-id="element-8167" data-mp="1v23lbyuje78q" />
                    {error && <p className="text-sm text-rose-600 font-medium text-left" data-id="element-8168" data-mp="2dzwdq77jayrv">
                        {error}
                      </p>}
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl" data-id="element-8169" data-mp="36h09z7mt6ufk">
                      <LinkIcon className="w-4 h-4 mr-2" data-id="element-8170" data-mp="o7w18eiyrolx" />
                      Koppelen
                    </Button>
                  </div>
                </form>
              </div>}
          </Card>
        </div>
      </PageLayout>;
  }
  // Djay greeting selection
  const getDjayMessage = () => {
    if (weeklyStats.practiceDays >= 3) {
      return {
        emotion: 'proud' as const,
        message: `Wat een topweek! ${selectedChild.displayName} heeft al ${weeklyStats.practiceDays} dagen geoefend.`
      };
    }
    if (moduleMastery.length > 0 && moduleMastery[0].mastery > 70) {
      return {
        emotion: 'happy' as const,
        message: `${selectedChild.displayName} is super goed bezig met ${moduleMastery[0].module.title.toLowerCase()}!`
      };
    }
    if (weeklyStats.practiceDays === 0) {
      return {
        emotion: 'encouraging' as const,
        message: `Tijd voor een oefensessie? 15 minuutjes per dag maakt al een groot verschil!`
      };
    }
    return {
      emotion: 'happy' as const,
      message: `Hier is het overzicht van hoe het gaat met ${selectedChild.displayName}.`
    };
  };
  const djayMessage = getDjayMessage();
  const currentStreak = childData.stats?.streakState?.currentStreak || 0;
  const cito = latestAssessment ? levelCodeToCitoLevel(latestAssessment.overallEstimatedLevelCode, latestAssessment.selectedGroup) : null;
  return <PageLayout title="Overzicht" description="Hoe gaat het deze week?" actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-8172" data-mp="tcq3xqjwi9al" />} data-id="element-8171" data-mp="ybt00lhwl56f">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8173" data-mp="188nr4jbuk3ao">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8174" data-mp="18a8m2a22po92" />

        {/* Hero — solid emerald, compact Djay, three chips (Level / XP / Streak) */}
        <Card className="overflow-hidden border-none shadow-md bg-emerald-600 relative" data-id="element-8175" data-mp="2epj5nwoo81i6">
          <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} data-id="element-8176" data-mp="2m8sf1px6g078" />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-8177" data-mp="2h8w7jgp9tqmx">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0" data-id="element-8178" data-mp="34yqj7uxut95l">
              <DjayNarrator size="md" emotion={djayMessage.emotion} data-id="element-8179" data-mp="39zxqlsw346bq" />
            </div>
            <div className="flex-1 text-center sm:text-left pt-1" data-id="element-8180" data-mp="2w4lw9mluchvc">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2" data-id="element-8181" data-mp="306z2ec4i2hsn">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-8182" data-mp="3nwtegrgzw2bu">
                  Hoe gaat het met {selectedChild.displayName}?
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap" data-id="element-8183" data-mp="6ex8rx2jb3t5">
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8184" data-mp="2dvabigfyvs8">
                    <Star className="w-3 h-3 fill-current" data-id="element-8185" data-mp="3tibqu1e3e6g0" /> Lvl{' '}
                    {childData.stats?.level || 1}
                  </span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8186" data-mp="3jlpbfs4jc7ld">
                    <Trophy className="w-3 h-3" data-id="element-8187" data-mp="3eu77qwb4nswp" /> {childData.stats?.xp || 0} XP
                  </span>
                  {currentStreak > 0 && <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8188" data-mp="35uapbcfbzqa3">
                      <Flame className="w-3 h-3" data-id="element-8189" data-mp="30u66vguv4lh8" /> {currentStreak} dag
                      {currentStreak === 1 ? '' : 'en'}
                    </span>}
                </div>
              </div>
              <p className="text-emerald-50 text-lg font-medium max-w-2xl leading-relaxed" data-id="element-8190" data-mp="2lro9i6zlvd42">
                {djayMessage.message}
              </p>
            </div>
          </div>
        </Card>

        {/* Compact weekly rhythm — minimal version; canonical detail on Activity */}
        <section data-id="element-8191" data-mp="30ofaulsuaywb">
          <div className="flex items-center justify-between mb-3" data-id="element-8192" data-mp="30oyj1asou8x5">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-8193" data-mp="232hmi7rs3zcg">
              <Clock className="w-4 h-4 text-emerald-500" data-id="element-8194" data-mp="281ujhlcd6msr" />
              Deze week
            </h2>
            <button onClick={() => onNavigate?.('activiteit')} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-8195" data-mp="2cyuqbyh1ftkx">
              Volledige kalender
              <ArrowRight className="w-3 h-3" data-id="element-8196" data-mp="1kaynsd4sf7g8" />
            </button>
          </div>
          <Card className="p-4 border-slate-200 shadow-sm bg-white" data-id="element-8197" data-mp="1p8qn3s7rhvug">
            <div className="flex justify-between items-center" data-id="element-8198" data-mp="33g02oygnld98">
              {weeklyActivity.map((day, i) => <div key={i} className="flex flex-col items-center gap-1.5 flex-1 min-w-0" data-id="element-8199" data-mp="2zght2yua8olp" data-mpi={i}>
                  <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-8200" data-mp="3dcl9npc8gyfp">
                    {day.label}
                  </div>
                  <div role="img" aria-label={day.practiced ? `${day.label}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${day.label}: nog niet geoefend${day.isToday ? ' (vandaag)' : ''}`} className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] font-black transition-all ${day.practiced ? 'bg-emerald-500 text-white shadow shadow-emerald-200' : day.isToday ? 'bg-white border-2 border-emerald-400 text-emerald-600' : 'bg-slate-50 border border-slate-100 text-slate-300'}`} data-id="element-8201" data-mp="3meuga44ds7nt">
                    {day.practiced ? day.questionCount : '·'}
                  </div>
                </div>)}
            </div>
          </Card>
        </section>

        {/* Decay alerts — only when there is something stale */}
        {decayAlerts.length > 0 && <section data-id="element-8202" data-mp="4wf891cr6tc7">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-8203" data-mp="4w5csy8xvtz4">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-8204" data-mp="9p8c7n0edxn8" />
              Aandachtspunt
            </h2>
            <div className="space-y-2" data-id="element-8205" data-mp="3s3hf5rit9u7g">
              {decayAlerts.map(alert => {
            const IconComp = getModuleIcon(alert.module.icon);
            const weeksAgo = Math.floor(alert.daysSince / 7);
            const timeLabel = weeksAgo >= 4 ? 'meer dan een maand' : weeksAgo >= 2 ? `${weeksAgo} weken` : 'meer dan een week';
            return <Card key={alert.module.id} onClick={() => onNavigate?.('voortgang')} className="p-4 border border-rose-200 bg-rose-50/60 cursor-pointer hover:bg-rose-50 transition-colors flex items-center gap-3 group" data-id="element-8206" data-mp="3r70y1ocah278">
                    <div className="w-10 h-10 rounded-xl bg-white text-rose-600 flex items-center justify-center flex-shrink-0 border border-rose-200" data-id="element-8207" data-mp="8sryltt33nrr">
                      <IconComp className="w-5 h-5" data-id="element-8208" data-mp="3v848fvbki612" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-8209" data-mp="3v606vk155j3y">
                      <div className="font-black text-sm text-rose-900 truncate" data-id="element-8210" data-mp="408l0wuecsmy">
                        {alert.module.title} dreigt weg te zakken
                      </div>
                      <div className="text-xs font-medium text-rose-700/80 truncate" data-id="element-8211" data-mp="widy6iddc50a">
                        {timeLabel} niet geoefend — 10 minuten herhalen helpt.
                      </div>
                    </div>
                    <History className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition-transform" data-id="element-8212" data-mp="2c853re06ezx6" />
                  </Card>;
          })}
            </div>
          </section>}

        {/* Tip + Latest Assessment — two compact slots side by side */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8213" data-mp="1y8lw0mba1qle">
          {topTip && <Card onClick={() => onNavigate?.('tips')} className={`p-5 cursor-pointer hover:shadow-md transition-shadow border bg-white group ${topTip.accent === 'amber' ? 'border-amber-200' : topTip.accent === 'rose' ? 'border-rose-200' : 'border-emerald-200'}`} data-id="element-8214" data-mp="1tb3u3fvogbmw">
              <div className="flex items-start gap-3 mb-3" data-id="element-8215" data-mp="3ioh0danco9qn">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${topTip.accent === 'amber' ? 'bg-amber-50 text-amber-600' : topTip.accent === 'rose' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-8216" data-mp="3ennfkq57fb1z">
                  {topTip.accent === 'rose' ? <Target className="w-5 h-5" data-id="element-8217" data-mp="2lbyst4bhlvl7" /> : topTip.accent === 'amber' ? <Clock className="w-5 h-5" data-id="element-8218" data-mp="2pbhei2b4uap3" /> : <Lightbulb className="w-5 h-5" data-id="element-8219" data-mp="1smrvu9role22" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-8220" data-mp="1xl344g2bvgi6">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8221" data-mp="1xluo0tmkbpsm">
                    Tip van Djay
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8222" data-mp="26e6hkocz3tct">
                    {topTip.title}
                  </h3>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mb-3" data-id="element-8223" data-mp="1dxc68x3vzeny">
                {topTip.body}
              </p>
              <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 group-hover:gap-1.5 transition-all" data-id="element-8224" data-mp="16e2xn637fjzy">
                <span data-id="element-8225" data-mp="11g1c8eib38lb">Alle tips</span>
                <ArrowRight className="w-3 h-3" data-id="element-8226" data-mp="11cx5b9dghyqy" />
              </div>
            </Card>}

          {isLoadingAssessment ?
        // v1.7 (P-2): skeleton while the latest-assessment fetch is in
        // flight — prevents the "Nog geen toets gemaakt" flash on mount.
        <Card className="p-5 border border-slate-200 bg-white" aria-busy="true" aria-label="Laatste toets laden" data-id="element-8227" data-mp="nh1nl6usmtp5">
              <div className="flex items-start gap-3 mb-3" data-id="element-8228" data-mp="dpoxajon8bl8">
                <Skeleton variant="rect" className="w-10 h-10" data-id="element-8229" data-mp="9o3p3bn20lhd" />
                <div className="flex-1 min-w-0 space-y-2" data-id="element-8230" data-mp="9lqy68zbj0s6">
                  <Skeleton className="w-20 h-3" data-id="element-8231" data-mp="4n5xy9k2gh3q" />
                  <Skeleton className="w-32 h-4" data-id="element-8232" data-mp="3vus5wedtj5qe" />
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8233" data-mp="3vx4p0uvbbm51">
                <div className="space-y-2" data-id="element-8234" data-mp="1zs5kuo734k20">
                  <Skeleton className="w-12 h-3" data-id="element-8235" data-mp="24qgw93d132mc" />
                  <Skeleton className="w-10 h-5" data-id="element-8236" data-mp="24q75hdk5srfl" />
                </div>
                <Skeleton variant="rect" className="w-14 h-9" data-id="element-8237" data-mp="1q2m0tf7myk5k" />
              </div>
            </Card> : latestAssessment ? <Card onClick={() => onNavigate?.('toetsen')} className="p-5 cursor-pointer hover:shadow-md transition-shadow border border-slate-200 bg-white group" data-id="element-8238" data-mp="1uz2qweyeb8la">
              <div className="flex items-start gap-3 mb-3" data-id="element-8239" data-mp="1l42c571nqu9i">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600" data-id="element-8240" data-mp="17400w3nuuz38">
                  <FileText className="w-5 h-5" data-id="element-8241" data-mp="1c2bbwhpvzfn1" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8242" data-mp="31i10iqr8bq4d">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8243" data-mp="31f6gh0c4o7bh">
                    Laatste toets
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8244" data-mp="utie73aweuvq">
                    {new Date(latestAssessment.completedAt).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long'
                })}{' '}
                    · Groep {latestAssessment.selectedGroup}
                  </h3>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8245" data-mp="18kx1hmbu9i8l">
                <div data-id="element-8246" data-mp="1nheba94dqot1">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8247" data-mp="1nhxstoq4bb6j">
                    Niveau
                  </div>
                  <div className="text-lg font-black text-slate-800" data-id="element-8248" data-mp="1ij2zvtyfs68p">
                    {latestAssessment.overallEstimatedLevelCode}
                  </div>
                </div>
                {cito && <div className={`flex items-center justify-center px-3 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8249" data-mp="3kbr9qm5l5rxg">
                    <span className={`text-lg font-black ${cito.color}`} data-id="element-8250" data-mp="hujbmrmo4d1z">
                      {cito.label}
                    </span>
                  </div>}
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8251" data-mp="rsgwr698dtqd" />
              </div>
            </Card> :
        // Placeholder when there's no assessment yet
        <Card className="p-5 border border-dashed border-slate-200 bg-slate-50/40 flex items-center gap-3" data-id="element-8252" data-mp="10rc4imrxbbvs">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-8253" data-mp="15kdld0boxli0">
                <FileText className="w-5 h-5" data-id="element-8254" data-mp="1fhrusfz75a0t" />
              </div>
              <div data-id="element-8255" data-mp="1fjmcllf68m0o">
                <div className="text-xs font-bold text-slate-500" data-id="element-8256" data-mp="1ffyjuohjw3n0">
                  Nog geen toets gemaakt
                </div>
                <div className="text-[11px] text-slate-400 leading-snug" data-id="element-8257" data-mp="34zgd4msnv4yj">
                  Zodra {selectedChild.displayName} een toets afrondt verschijnt
                  hier het niveau.
                </div>
              </div>
            </Card>}
        </section>

        {/* Slim explore-row — replaces the duplicate stat-cards. No numbers here:
            this is pure navigation, not a stat-restate. */}
        <section data-id="element-8258" data-mp="3no3dwt3t186b">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8259" data-mp="3nomx0d56y2lx">
            Verder kijken
          </h2>
          <div className={`grid gap-3 ${linkedLearners.length >= 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`} data-id="element-8260" data-mp="3dzlikreanta2">
            <button onClick={() => onNavigate?.('voortgang')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 focus-visible:outline-none focus-visible:border-emerald-400 focus-visible:bg-emerald-50/60 focus-visible:ring-2 focus-visible:ring-emerald-300 transition-all text-left group" data-id="element-8261" data-mp="38zz5chygud19">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-8262" data-mp="3etmdna53l2cz">
                <TrendingUp className="w-4 h-4" data-id="element-8263" data-mp="36010twchtv67" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8264" data-mp="3609bdhl58oe8">
                <div className="text-sm font-black text-slate-900" data-id="element-8265" data-mp="3tp2dxww9tuht">Vakken</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8266" data-mp="1lt1vg5n5m5o">
                  Per-vak beheersing
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8267" data-mp="3jqcw244dka1k" />
            </button>

            <button onClick={() => onNavigate?.('activiteit')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:border-blue-400 focus-visible:bg-blue-50/60 focus-visible:ring-2 focus-visible:ring-blue-300 transition-all text-left group" data-id="element-8268" data-mp="3oslpf1l5te47">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-8269" data-mp="20sgh05q84v1v">
                <CalendarDays className="w-4 h-4" data-id="element-8270" data-mp="29teq32firsrb" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8271" data-mp="29u7mpakrl1vq">
                <div className="text-sm font-black text-slate-900" data-id="element-8272" data-mp="1m3c2ozmjvaz1">
                  Activiteit
                </div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8273" data-mp="1i39vyc1yjrcw">
                  Kalender & reeksen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-8274" data-mp="1vvq1pkm8u3hi" />
            </button>

            <button onClick={() => onNavigate?.('tips')} className={`flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40 focus-visible:outline-none focus-visible:border-amber-400 focus-visible:bg-amber-50/60 focus-visible:ring-2 focus-visible:ring-amber-300 transition-all text-left group ${linkedLearners.length >= 2 ? '' : 'col-span-2 sm:col-span-1'}`} data-id="element-8275" data-mp="13d2lojm3tena">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-8276" data-mp="3irqneci3c46w">
                <Lightbulb className="w-4 h-4" data-id="element-8277" data-mp="fd4i66p42a19" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8278" data-mp="fcbexfpd22nv">
                <div className="text-sm font-black text-slate-900" data-id="element-8279" data-mp="adi45v1gkpre">Tips</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8280" data-mp="6ceksyk7cblf">
                  Alle adviezen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors" data-id="element-8281" data-mp="1ca5hbv15mws" />
            </button>

            {/* W4-1 — Vergelijken: only surfaces for multi-child accounts (Mara persona) */}
            {linkedLearners.length >= 2 && <button onClick={() => onNavigate?.('compare')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 focus-visible:outline-none focus-visible:border-violet-400 focus-visible:bg-violet-50/60 focus-visible:ring-2 focus-visible:ring-violet-300 transition-all text-left group" data-id="element-8282" data-mp="11ls0j6xa6o56">
                <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0" data-id="element-8283" data-mp="1kcj7k1tsq2ge">
                  <Users className="w-4 h-4" data-id="element-8284" data-mp="1p9t5y6z5xogd" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8285" data-mp="1u9nq7rofnfsg">
                  <div className="text-sm font-black text-slate-900" data-id="element-8286" data-mp="1uayzg70ndfqy">
                    Vergelijken
                  </div>
                  <div className="text-xs text-slate-500 truncate" data-id="element-8287" data-mp="3w1k5ncdq19si">
                    Kinderen naast elkaar
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors" data-id="element-8288" data-mp="3y9rduzpjk0n" />
              </button>}
          </div>
        </section>
      </div>
    </PageLayout>;
});